<template>
    <Modal v-model:isOpen="isOpen" @close="onClose">
      <template #title>
        {{ title }}
      </template>
      <template #subtitle>
        {{ subtitle }}
      </template>
      <template #content>
        <div v-if="!hasEntity">No order, trip or container found to show notes for</div>
        <AddNotesArea
          v-else
          :notes="localNotes"
          :archivedNotes="localArchivedNotes"
          v-bind="entityBindings"
          @addedNote="onAddedNote"
          @archivedNotesUpdated="onArchivedNotesUpdated"
        />
      </template>
    </Modal>      
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AddNotesArea from './AddNotesArea.vue';
import Modal from './Modal/Modal.vue';

type Props = {
  open: boolean;
  title: string;
  subtitle?: string;
  trip?: any;
  order?: any;
  container?: any;
};

const props = defineProps<Props>();
const isOpen = ref<boolean>(props.open);

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'addedNote', notes: any[]): void;
  (e: 'archivedNotesUpdated', archivedNotes: any[]): void;
}>();

const localNotes = ref<any[]>([]);
const localArchivedNotes = ref<any[]>([]);

const hasEntity = computed(() => !!(props.trip || props.order || props.container));

const entityBindings = computed(() => {
  if (props.trip) return { trip: props.trip };
  if (props.order) return { order: props.order };
  if (props.container) return { container: props.container };
  return {};
});

function onClose() {
  emit('close');
}

function onAddedNote(notes: any[]) {
  const list = Array.isArray(notes) ? notes : [];
  localNotes.value = list;
  emit('addedNote', list);
}

function onArchivedNotesUpdated(archivedNotes: any[]) {
  const list = Array.isArray(archivedNotes) ? archivedNotes : [];
  localArchivedNotes.value = list;
  emit('archivedNotesUpdated', list);
}

/**
 * Same underlying Note type, different parent relations in the API:
 * Trip -> `tripNotes`
 * Order/ContainerLoad -> `notes`
 * (AddNotesArea picks the mutation from whichever of trip | order | container is passed)
 */
function readNotesFromProps(trip?: any, order?: any, container?: any): any[] {
  if (trip) {
    return Array.isArray(trip.tripNotes) ? trip.tripNotes : [];
  }
  const orderOrContainer = order ?? container;
  if (orderOrContainer) {
    return Array.isArray(orderOrContainer.notes) ? orderOrContainer.notes : [];
  }
  return [];
}

function readArchivedNotesFromProps(trip?: any, order?: any, container?: any): any[] {
  if (trip) {
    return Array.isArray(trip.archivedTripNotes) ? trip.archivedTripNotes : [];
  }
  const orderOrContainer = order ?? container;
  if (orderOrContainer) {
    return Array.isArray(orderOrContainer.archivedNotes) ? orderOrContainer.archivedNotes : [];
  }
  return [];
}

function syncFromEntity() {
  localNotes.value = readNotesFromProps(props.trip, props.order, props.container);
  localArchivedNotes.value = readArchivedNotesFromProps(props.trip, props.order, props.container);
}

watch(
  () => [props.trip, props.order, props.container],
  () => syncFromEntity(),
  { immediate: true, deep: true },
);

watch(
  () => props.open,
  (open) => { isOpen.value = open },
);
</script>
